/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { NodeModel, TemplateModel } from '../../modules/loadbalancer/shared';
import { Observable, of, Subject } from 'rxjs';
import { ValidationService } from '../services';

@Injectable()
export class LoadbalancerMockService {
    /**
     * Mock language service.
     */
    public aclService(): any {
        return (
            class MockAclService {
                addEditAcl() {
                }
            }
        );
    }

    /**
     * Mock the cluster service.
     */
    public clusterService(): any {
        return (
            class MockClusterService {
                public pulseStatusEmitter: Subject<any> = new Subject<any>();
                public nodesEmitter: Subject<NodeModel[]> = new Subject<NodeModel[]>();
                public localNodeEmitter: Subject<NodeModel> = new Subject<NodeModel>();

                get localNode(): NodeModel {
                    return {
                        'id': 'a0d-011-873-401',
                        'hostname': 'lbnitwofournine',
                        'ip': '192.168.98.249',
                        'port': 443,
                        'lastReceived': '',
                        'latency': '',
                        'status': 'active',
                        'statusCode': 0,
                        'applianceVersion': '9.0.3.1542.g4aabc9f26',
                        'clientVersion': '9.0.1.10238.g4e250259f',
                        'services': [
                            {
                                name: 'gslb',
                                fullName: 'GSLB',
                                status: 'active',
                                action: '',
                                canReload: true,
                                messages: ['Test message']
                            },
                            {
                                name: 'haproxy',
                                fullName: 'HA Proxy',
                                status: 'active',
                                action: '',
                                canReload: true,
                                messages: ['Test message']
                            },
                            {
                                name: 'nginx_fallback',
                                fullName: 'Nginx Fallback',
                                status: 'disabled',
                                action: '',
                                canReload: true,
                                messages: ['Test message']
                            },
                            {
                                name: 'pulseha',
                                fullName: 'PulseHA',
                                status: 'active',
                                action: '',
                                canReload: true,
                                messages: ['Test message']
                            },
                        ]
                    };
                }

                _nodes: NodeModel[] = [];

                get nodes(): NodeModel[] {
                    return this._nodes;
                }

                isNodeInCluster(): boolean {
                    return false;
                }
            });
    }

    /**
     * Mock certificates service.
     */
    public certsService(): any {
        return (
            class MockApiService {
                getCSR() {
                    return new Subject<any>();
                }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
                getCert(certId: string) {
                    return new Subject<any>();
                }

                getCSRs() {
                    return new Subject<any>();
                }

                getCerts() {
                    return new Subject<any>();
                }

                getSignatureAlgorithms() {
                    return new Subject<any>();
                }
            }
        );
    }

    /**
     * Mock FIPApiService
     */
    public fipApiService(): any {
        return (
            class MockFIPApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                editIP(ipId, { ip, groupId: group }): any {
                    return;
                }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
                addIP({ ip, groupId: group }): any {
                    return;
                }

                getFloatingIPs(): Observable<any> {
                    return new Observable<any>();
                }
            }
        );
    }

    /**
     * Mock FloatingIpsService
     */
    public floatingIpsService(): any {
        return (
            class MockFloatingIpsService {
                getFloatingIpData(): Observable<any> {
                    return new Observable<any>();
                }
            }

        );
    }

    /**
     * Mock lbErrorService service
     */
    public lbErrorService(): any {
        return (
            class MockLbErrorService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                formatErrors(errorArray: any[]): any [] {
                    return [];
                }

                pageWithErrors() {
                }
            }
        );
    }

    /**
     * Mock networking api service
     */
    public networkingApiService(): any {
        return (
            class NetworkingApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                assignGroupInterface(interfaces: any, groupId?: string): any {
                    return '1d8-088-481-c1d';
                }

                getInterfaces(): any {
                    return [
                        { 'id': '09e-11f-6fa-952', 'name': 'Management' },
                        { 'id': 'e61-229-339-a0c', 'name': 'network-1' },
                        { 'id': '0e0-759-506-3b6', 'name': 'network-2' },
                        { 'id': '7f3-216-d96-a28', 'name': 'network-3' }
                    ];
                }
            }
        );
    }

    /**
     * Mock networking api service
     */
    public networkingService(): any {
        return (
            class NetworkingService {
                getLocalAssignedInterface() {
                    return [];
                }

                sortDNS(formDns: any): any {
                    const dnsIPv4: string[] = [];
                    const dnsIPv6: string[] = [];
                    for (const dns of formDns) {
                        if (ValidationService.ipV4Check(dns)) {
                            dnsIPv4.push(dns);
                        } else {
                            dnsIPv6.push(dns);
                        }
                    }

                    return { dnsIPv4: dnsIPv4, dnsIPv6: dnsIPv6 };
                }
            }
        );
    }

    /**
     * Mock licence service.
     */
    public licenceService(): any {
        return (
            class MockLicenceService {
                _licenceObj: any = {
                    'platform': 'vmware',
                    'serial': '7de-456-38f-0bd',
                    'daysToExpire': 25,
                    'companyName': 'Acme Inc',
                    'supportExpireDate': '16/12/2023',
                    'maxVips': 0,
                    'maxRips': 0,
                    'licenceType': 'trial',
                    'model': '100g',
                    'modules': []
                };

                get licenceObj() {
                    return this._licenceObj;
                }
            }
        );
    }

    /**
     * Mock the rbac service.
     */
    public lbRbacService(): any {
        return (
            class RbacService {
                enableButtonsInTheLookUp(): any {
                    return true;
                }

                getPageRequiredRoutes(): any {
                    return {};
                }

                setButtonStateInLookup(): any {
                    return;
                }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
                isMenuItemVisible(item: string): any {
                    return true;
                }
            }
        );
    }

    /**
     * Mock prompt service.
     */
    public promptService(): any {
        return (
            class MockPromptService {
                serviceStatusEmitter: Subject<object> = new Subject<object>();
                localServiceStatusEmitter: Subject<object> = new Subject<object>();

                _nodes: any[] = [];

                get nodes(): any[] {
                    return this._nodes;
                }
            }
        );
    }

    /**
     * Mock ServicesApiService
     */
    public servicesApiService(): any {
        return (
            class MockServicesApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                restartService(service: string): void {
                }
            }
        );
    }

    /**
     * Mock SIPApiService
     */
    public sipApiService(): any {
        return (
            class MockSIPApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                editIP(ipId, { ip, groupId: group }): any {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                addIP({ ip, groupId: group }): any {
                    return;
                }

                getStaticIPs(): Observable<any> {
                    return new Observable<any>();
                }
            }

        );
    }

    /**
     * Mock StaticRoutesApiService
     */
    public staticRoutesApiService(): any {
        return (
            class MockStaticRoutesApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                editRoute(routeId, { gateway, type, groupId }): any {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                addRoute({ gateway, type, groupId }): any {
                    return;
                }

                getRoutes(): Observable<any> {
                    return new Observable<any>();
                }
            }

        );
    }

    /**
     * Mock StaticRoutesService
     */
    public staticRoutesService(): any {
        return (
            class MockStaticRoutesService {
                getStaticRouteData(): Observable<any> {
                    return new Observable<any>();
                }
            }
        );
    }

    /**
     * Mock the SNMP configuration service.
     */
    public snmpConfigurationApiService(): any {
        return (
            class SNMPConfigurationApiService {
                getSNMP(): Observable<any> {
                    return of(null);
                }

                addSNMP(): Observable<any> {
                    return of(null);
                }
            }
        );
    }

    /**
     * Mock StaticIpsService
     */
    public staticIpsService(): any {
        return (
            class MockStaticIpsService {
                getStaticIpData(): Observable<any> {
                    return new Observable<any>();
                }
            }
        );
    }

    /**
     * Mock StaticIpsService
     */
    public wizardService(): any {
        return (
            class MockWizardService {
                parentStepEmitter: Subject<any> = new Subject<any>();
                subStepNameEmitter: Subject<any> = new Subject<any>();
                wizardModeEmitter: Subject<any> = new Subject<any>();
                errorNotificationClosed: Subject<any> = new Subject<any>();
                isLoading: boolean = false;

                _controllerState: any = {};
                get controllerState(): any {
                    return this._controllerState;
                }

                set controllerState(state: any) {
                    this._controllerState = state;
                }

                _selectedTemplate: TemplateModel = null;
                get selectedTemplate(): TemplateModel {
                    return this._selectedTemplate;
                }

                _disableNavigation;
                set disableNavigation(values) {
                    this._disableNavigation = values;
                }

                nextStep() {

                }

                resetWizardService(): void {

                }
            }
        );
    }

    /**
     * Mock StaticIpsService
     */
    public wizardSubmissionService(): any {
        return (
            class MockWizardSubmissionService {
                transactionState: Subject<any> = new Subject<any>();
            }
        );
    }

    /**
     * Wizard layer 4 and 7 service.
     */
    public layer47WizardService(): any {
        return (
            class Layer47WizardService {

            }
        );
    }

    /**
     * Wizard layer 4 and 7 service.
     */
    public layer47SubmissionService(): any {
        return (
            class Layer47SubmissionService {

            }
        );
    }

    /**
     * Wizard GSLB submission service.
     */
    public gslbSubmissionService(): any {
        return (
            class GSLBSubmissionService {

            }
        );
    }

    /**
     * Wizard lb save service
     */
    public wizardLbSaveService(): any {
        return (
            class WizardLbSaveService {

            }
        );
    }
}
